import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FiList, FiChevronDown, FiPlus, FiPlusCircle, FiSearch, FiRefreshCw,
  FiCalendar, FiArrowUp, FiArrowRight,
  FiArrowDown, FiAlertTriangle, FiChevronRight, FiUser, FiUsers, FiTrash2,
  FiFilter, FiX
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { fmtDate } from '../../utils/istDate';
import CreateTaskModal from './CreateTaskModal';
import ConfirmModal from '../shared/ConfirmModal';
import tasksApi, { canEditTask, canChangeTaskStatus } from '../../api/tasksApi';
import TaskChecklistIcon from '../shared/TaskChecklistIcon';

// Refined, light modern badges (related, minimal, light colors per Xpertree design rules)
export const STATUS_META = {
  TO_DO: { label: 'To Do', color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400' },
  IN_PROGRESS: { label: 'In Progress', color: 'text-blue-700', bg: 'bg-blue-50/60', border: 'border-blue-200', dot: 'bg-blue-500' },
  COMPLETED: { label: 'Completed', color: 'text-emerald-700', bg: 'bg-emerald-50/60', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  BLOCKED: { label: 'Blocked', color: 'text-rose-700', bg: 'bg-rose-50/60', border: 'border-rose-200', dot: 'bg-rose-500' },
  // Backward compatibility fallbacks
  CREATED: { label: 'To Do', color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400' },
  NOT_STARTED: { label: 'To Do', color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400' },
};

// Clean, modern text badges without distracting arrows
export const PRIORITY_META = {
  CRITICAL: { label: 'Critical', color: 'text-rose-700', bg: 'bg-rose-50/60', border: 'border-rose-200' },
  HIGH: { label: 'High', color: 'text-amber-700', bg: 'bg-amber-50/60', border: 'border-amber-200' },
  MEDIUM: { label: 'Medium', color: 'text-blue-700', bg: 'bg-blue-50/60', border: 'border-blue-200' },
  LOW: { label: 'Low', color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' }
};

export const STATUS_FILTER_OPTIONS = [
  { id: 'ALL', label: 'All Statuses' },
  { id: 'TO_DO', label: 'To Do' },
  { id: 'IN_PROGRESS', label: 'In Progress' },
  { id: 'COMPLETED', label: 'Completed' },
  { id: 'BLOCKED', label: 'Blocked' },
  { id: 'OVERDUE', label: 'Overdue' }
];

export const PRIORITY_FILTER_OPTIONS = [
  { id: 'ALL', label: 'All Priorities' },
  { id: 'CRITICAL', label: 'Critical' },
  { id: 'HIGH', label: 'High' },
  { id: 'MEDIUM', label: 'Medium' },
  { id: 'LOW', label: 'Low' }
];

// Clean, unified neutral avatar
function Avatar({ name, size = 30 }) {
  return (
    <div
      className={`font-semibold flex items-center justify-center shrink-0 rounded-full border ${name
          ? 'bg-slate-100 text-slate-700 border-slate-300'
          : 'bg-slate-50 text-slate-400 border-dashed border-slate-200'
        }`}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      title={name || 'Unassigned'}
    >
      {name ? name.slice(0, 2).toUpperCase() : '—'}
    </div>
  );
}

export default function MySpaceTasks() {
  const navigate = useNavigate();

  // Role & Session Detection
  const currentUsername = localStorage.getItem('username') || '';
  const userRole = (localStorage.getItem('role') || '').toUpperCase();
  const isSuperAdmin = userRole.includes('SUPERADMIN') || userRole.includes('TESTER');
  const isAdmin = userRole.includes('ADMIN');
  const isManager = userRole.includes('MANAGER');

  // Team tasks visible ONLY to SuperAdmin, Admin, or Manager — NEVER to regular users
  const canViewTeamTasks = isSuperAdmin || isAdmin || isManager;

  // Tab State: 'my-tasks' (Assigned to Me) vs 'created-by-me' vs 'team-tasks' (Team - Admin/Manager only)
  const [activeCategory, setActiveCategory] = useState(canViewTeamTasks ? 'team-tasks' : 'my-tasks');

  // Team tab is enabled for both users (shows copy-to tasks) and admins (subordinates + copy-to)

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // Local Storage Filter Preferences Key
  const FILTER_STORAGE_KEY = 'orakris_task_filters_preferences';

  // Helper to load persisted filter preferences from localStorage
  const getInitialFilters = () => {
    try {
      const saved = localStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          status: parsed.status || 'ALL',
          priority: parsed.priority || 'ALL',
          project: parsed.project || 'ALL',
          product: parsed.product || 'ALL',
          team: parsed.team || 'ALL',
          employee: parsed.employee || 'ALL'
        };
      }
    } catch (e) {
      console.error('Failed to parse saved task filters:', e);
    }
    return {
      status: 'ALL',
      priority: 'ALL',
      project: 'ALL',
      product: 'ALL',
      team: 'ALL',
      employee: 'ALL'
    };
  };

  // Filters & State (Persisted in localStorage)
  const initialFilters = getInitialFilters();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialFilters.status);
  const [priorityFilter, setPriorityFilter] = useState(initialFilters.priority);
  const [projectFilter, setProjectFilter] = useState(initialFilters.project);
  const [productFilter, setProductFilter] = useState(initialFilters.product);
  const [teamFilter, setTeamFilter] = useState(initialFilters.team);
  const [employeeFilter, setEmployeeFilter] = useState(initialFilters.employee);

  // Synchronize filter selections to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({
          status: statusFilter,
          priority: priorityFilter,
          project: projectFilter,
          product: productFilter,
          team: teamFilter,
          employee: employeeFilter
        })
      );
    } catch (e) {
      console.warn('Failed to save task filter preferences:', e);
    }
  }, [statusFilter, priorityFilter, projectFilter, productFilter, teamFilter, employeeFilter]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterBtnRef = useRef(null);
  const filterPopoverRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, right: 0 });
  const [projectsList, setProjectsList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const updatePopoverPos = useCallback(() => {
    if (filterBtnRef.current) {
      const rect = filterBtnRef.current.getBoundingClientRect();
      const rightPos = Math.max(12, window.innerWidth - rect.right);
      setPopoverPos({
        top: rect.bottom + 8,
        right: rightPos
      });
    }
  }, []);

  const handleToggleFilter = () => {
    if (!isFilterOpen) {
      updatePopoverPos();
    }
    setIsFilterOpen(prev => !prev);
  };

  // Keep popover pinned to button during scroll or resize
  useEffect(() => {
    if (!isFilterOpen) return;
    const handleScrollOrResize = () => updatePopoverPos();
    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [isFilterOpen, updatePopoverPos]);

  // Click outside to close filter popover
  useEffect(() => {
    if (!isFilterOpen) return;
    const handleClickOutside = (e) => {
      if (
        filterPopoverRef.current &&
        !filterPopoverRef.current.contains(e.target) &&
        filterBtnRef.current &&
        !filterBtnRef.current.contains(e.target)
      ) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFilterOpen]);

  // Fetch Tasks based on active category
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let loaded = [];
      if (activeCategory === 'my-tasks') {
        loaded = await tasksApi.getMyTasks();
      } else if (activeCategory === 'created-by-me') {
        loaded = await tasksApi.getCreatedByMeTasks();
      } else if (activeCategory === 'team-tasks') {
        loaded = await tasksApi.getTeamTasks();
      }
      const items = Array.isArray(loaded) ? loaded : (loaded?.content || []);
      setTasks(items);
    } catch (err) {
      console.error(err);
      setTasks([]);
      if (err.status === 401 || err.status === 403) {
        setError('Authentication session expired. Please log in again.');
      } else {
        setError('Could not fetch tasks. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [activeCategory, canViewTeamTasks]);

  // Reset page when category or filters change
  useEffect(() => {
    setPage(1);
  }, [activeCategory, search, statusFilter, priorityFilter, projectFilter, productFilter, teamFilter, employeeFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Load assignees from dedicated authenticated endpoint
  useEffect(() => {
    tasksApi.getAssignees()
      .then(res => {
        const data = Array.isArray(res) ? res : (res?.data || []);
        if (Array.isArray(data)) setUsers(data);
      })
      .catch(err => {
        console.error('Failed to prefetch assignees:', err);
      });

    tasksApi.getProjects()
      .then(res => {
        const data = Array.isArray(res) ? res : (res?.data || []);
        if (Array.isArray(data)) setProjectsList(data);
      })
      .catch(err => console.warn('Failed loading projects:', err));

    tasksApi.getProducts()
      .then(res => {
        const data = Array.isArray(res) ? res : (res?.data || []);
        if (Array.isArray(data)) setProductsList(data);
      })
      .catch(err => console.warn('Failed loading products:', err));
  }, []);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, taskId: null, taskTitle: '', loading: false });

  // Handle task deletion modal open
  const openDeleteModal = (e, taskItem) => {
    e.stopPropagation();
    setDeleteConfirm({
      open: true,
      taskId: taskItem.taskId,
      taskTitle: taskItem.title || `Task #${taskItem.taskId}`,
      loading: false
    });
  };

  // Handle task deletion confirm
  const handleConfirmDeleteTask = async () => {
    const { taskId: id, taskTitle } = deleteConfirm;
    if (!id) return;
    setDeleteConfirm(prev => ({ ...prev, loading: true }));
    try {
      await tasksApi.deleteTask(id);
      toast.success(`Task "${taskTitle}" deleted.`);
      setTasks(prev => prev.filter(t => t.taskId !== id));
      setDeleteConfirm({ open: false, taskId: null, taskTitle: '', loading: false });
    } catch (err) {
      const errMsg = err?.response?.data || err?.message || 'Failed to delete task';
      toast.error(typeof errMsg === 'string' ? errMsg : 'Failed to delete task');
      setDeleteConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle in-place task status change directly from table dropdown
  const handleUpdateStatus = async (e, taskItem, newStatus) => {
    if (e) e.stopPropagation();
    if (!taskItem || taskItem.taskStatus === newStatus) return;
    if (!canEditTask(taskItem, currentUsername)) {
      toast.error('Only the task assignee or creator can edit this task.');
      return;
    }
    try {
      await tasksApi.updateTaskStatus(taskItem.taskId, newStatus);
      setTasks(prev => prev.map(t => t.taskId === taskItem.taskId ? { ...t, taskStatus: newStatus } : t));
      toast.success(`Status updated to ${STATUS_META[newStatus]?.label || newStatus}`);
    } catch (err) {
      toast.error('Failed to update task status');
    }
  };

  // Handle task creation (supports attachments & initial subtasks)
  const handleSaveTask = async (taskPayload, files, subtasksList = []) => {
    try {
      const createdTask = await tasksApi.createTask(taskPayload);

      // Create subtasks added in the modal before attachments
      if (subtasksList && subtasksList.length > 0 && createdTask?.taskId) {
        for (const st of subtasksList) {
          const title = typeof st === 'string' ? st : st.title;
          if (title && title.trim()) {
            try {
              await tasksApi.createSubtask({
                taskId: createdTask.taskId,
                subTaskTitle: title.trim(),
                subTaskStatus: 'TO_DO'
              });
            } catch (stErr) {
              console.error('Failed creating subtask:', stErr);
            }
          }
        }
      }

      // Upload attachments if provided
      if (files && files.length > 0 && createdTask?.taskId) {
        for (const file of files) {
          try {
            await tasksApi.uploadAttachment(createdTask.taskId, file);
          } catch (attErr) {
            console.error('Failed uploading attachment:', attErr);
          }
        }
      }

      toast.success(`Task created.`);
      setIsModalOpen(false);
      fetchTasks();
    } catch (err) {
      toast.error(err.message || 'Failed to create task.');
      throw err;
    }
  };

  // Extract unique teams / departments from users and tasks
  const uniqueTeams = useMemo(() => {
    const set = new Set();
    users.forEach(u => {
      if (u.department && u.department.trim()) {
        set.add(u.department.trim());
      }
    });
    tasks.forEach(t => {
      if (t.department && t.department.trim()) {
        set.add(t.department.trim());
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [users, tasks]);

  // Extract unique employees (filtered by selected team if teamFilter !== 'ALL')
  const uniqueEmployees = useMemo(() => {
    const map = new Map();
    users.forEach(u => {
      if (u.username) {
        map.set(u.username, {
          username: u.username,
          fullName: u.fullName || u.username,
          department: u.department || ''
        });
      }
    });
    tasks.forEach(t => {
      if (t.username && !map.has(t.username)) {
        map.set(t.username, {
          username: t.username,
          fullName: t.assigneeName || t.username,
          department: t.department || ''
        });
      }
    });
    let list = Array.from(map.values());
    if (teamFilter !== 'ALL') {
      list = list.filter(e => (e.department || '').toUpperCase() === teamFilter.toUpperCase());
    }
    return list.sort((a, b) => (a.fullName || a.username).localeCompare(b.fullName || b.username));
  }, [users, tasks, teamFilter]);

  // Context-filtered tasks: filters by all active non-status filters (search, priority, project, product, team, employee)
  // This allows the status pill counts (All, To Do, In Progress, Completed, Overdue) to dynamically reflect the active filter context
  const contextFilteredTasks = useMemo(() => {
    const query = (search || '').toLowerCase().trim();

    return tasks.filter(t => {
      // 1. Search Query
      if (query) {
        const titleMatch = (t.title || '').toLowerCase().includes(query) ||
          (t.description || '').toLowerCase().includes(query) ||
          (t.username || '').toLowerCase().includes(query) ||
          (t.assigneeName || '').toLowerCase().includes(query);
        if (!titleMatch) return false;
      }

      // 2. Priority Filter
      const taskPri = t.priority || 'MEDIUM';
      if (priorityFilter !== 'ALL' && taskPri !== priorityFilter) {
        return false;
      }

      // 3. Project Filter
      if (projectFilter !== 'ALL' && String(t.projectId) !== String(projectFilter)) {
        return false;
      }

      // 4. Product Filter
      if (productFilter !== 'ALL' && String(t.productId) !== String(productFilter)) {
        return false;
      }

      // 5. Employee Filter (applies ONLY on team-tasks)
      if (activeCategory === 'team-tasks' && employeeFilter !== 'ALL' && t.username !== employeeFilter) {
        return false;
      }

      // 6. Team Filter (applies ONLY on team-tasks)
      if (activeCategory === 'team-tasks' && teamFilter !== 'ALL') {
        const taskDept = (t.department || users.find(u => u.username === t.username)?.department || '').trim();
        if (taskDept.toUpperCase() !== teamFilter.toUpperCase()) {
          return false;
        }
      }

      return true;
    });
  }, [tasks, search, priorityFilter, projectFilter, productFilter, teamFilter, employeeFilter, activeCategory, users]);

  // Summary KPI statistics computed for current filtered context (matching Xpertree style)
  const stats = useMemo(() => {
    let total = contextFilteredTasks.length;
    let todo = 0;
    let inProgress = 0;
    let completed = 0;
    let overdue = 0;
    const nowZero = new Date().setHours(0, 0, 0, 0);

    for (const t of contextFilteredTasks) {
      let stat = t.taskStatus || 'TO_DO';
      if (stat === 'CREATED' || stat === 'NOT_STARTED') stat = 'TO_DO';

      if (stat === 'COMPLETED') {
        completed++;
      } else if (stat === 'IN_PROGRESS') {
        inProgress++;
      } else {
        todo++;
      }
      if (stat !== 'COMPLETED' && t.deadLine && new Date(t.deadLine).setHours(0, 0, 0, 0) < nowZero) {
        overdue++;
      }
    }
    return { total, todo, inProgress, completed, overdue };
  }, [contextFilteredTasks]);

  // Final filtered tasks: applying status filter to context-filtered tasks and sorting descending by creation (taskId DESC)
  const filteredTasks = useMemo(() => {
    const nowZero = new Date().setHours(0, 0, 0, 0);

    return contextFilteredTasks.filter(t => {
      if (statusFilter === 'ALL') return true;

      let taskStat = t.taskStatus || 'TO_DO';
      if (taskStat === 'CREATED' || taskStat === 'NOT_STARTED') taskStat = 'TO_DO';

      if (statusFilter === 'OVERDUE') {
        return Boolean(t.deadLine && new Date(t.deadLine).setHours(0, 0, 0, 0) < nowZero && taskStat !== 'COMPLETED');
      }

      return taskStat === statusFilter;
    }).sort((a, b) => (Number(b.taskId) || 0) - (Number(a.taskId) || 0));
  }, [contextFilteredTasks, statusFilter]);

  // Pagination calculations
  const totalFilteredTasks = filteredTasks.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredTasks / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const paginatedTasks = filteredTasks.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Active Filter Chips computation
  const activeFilters = [];
  if (projectFilter !== 'ALL') {
    const proj = projectsList.find(p => String(p.id) === String(projectFilter));
    activeFilters.push({
      key: 'project',
      label: `Project: ${proj?.projectName || proj?.name || `Project #${projectFilter}`}`,
      onRemove: () => setProjectFilter('ALL')
    });
  }
  if (productFilter !== 'ALL') {
    const prod = productsList.find(p => String(p.id) === String(productFilter));
    activeFilters.push({
      key: 'product',
      label: `Product: ${prod?.productname || prod?.name || `Product #${productFilter}`}`,
      onRemove: () => setProductFilter('ALL')
    });
  }
  if (activeCategory === 'team-tasks') {
    if (teamFilter !== 'ALL') {
      activeFilters.push({
        key: 'team',
        label: `Team: ${teamFilter}`,
        onRemove: () => setTeamFilter('ALL')
      });
    }
    if (employeeFilter !== 'ALL') {
      const emp = users.find(u => u.username === employeeFilter);
      activeFilters.push({
        key: 'employee',
        label: `Employee: ${emp?.fullName || employeeFilter}`,
        onRemove: () => setEmployeeFilter('ALL')
      });
    }
  }
  if (statusFilter !== 'ALL') {
    const sm = STATUS_META[statusFilter];
    activeFilters.push({
      key: 'status',
      label: `Status: ${statusFilter === 'OVERDUE' ? 'Overdue' : (sm?.label || statusFilter)}`,
      onRemove: () => setStatusFilter('ALL')
    });
  }
  if (priorityFilter !== 'ALL') {
    const pm = PRIORITY_META[priorityFilter];
    activeFilters.push({
      key: 'priority',
      label: `Priority: ${pm?.label || priorityFilter}`,
      onRemove: () => setPriorityFilter('ALL')
    });
  }
  const activeFilterCount = activeFilters.length;

  const clearAllFilters = () => {
    setStatusFilter('ALL');
    setPriorityFilter('ALL');
    setProjectFilter('ALL');
    setProductFilter('ALL');
    setTeamFilter('ALL');
    setEmployeeFilter('ALL');
  };

  return (
    <div className="space-y-5 animate-fade-in pb-16">

      {/* Header - Balanced, Clean, Professional */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 sm:p-6 rounded-2xl border border-slate-200/90 shadow-xs">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Tasks</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Track deliverables, manage workflow status, and collaborate with your team.</p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          {/* Primary Action Button: Clean Blue (Never Thick Black) */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-xs sm:text-sm transition-all shadow-xs cursor-pointer"
          >
            <FiPlus className="text-base" />
            <span>Create Task</span>
          </button>
        </div>
      </div>

      {/* Segmented Tabs: Team, Created by Me, Assigned to Me (Shifted for Superadmin/Admin/Manager) */}
      <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-xl border border-slate-200/80 overflow-x-auto max-w-full">
        {canViewTeamTasks ? (
          <>
            {/* 1. Team Tab (First for Superadmin/Admin/Manager) */}
            <button
              onClick={() => setActiveCategory('team-tasks')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer ${activeCategory === 'team-tasks'
                  ? 'bg-white text-slate-900 shadow-xs border border-slate-200/80'
                  : 'text-slate-600 hover:text-slate-900'
                }`}
            >
              <FiUsers className="text-sm" />
              <span>Team</span>
            </button>

            {/* 2. Created by Me */}
            <button
              onClick={() => {
                setActiveCategory('created-by-me');
                setTeamFilter('ALL');
                setEmployeeFilter('ALL');
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer ${activeCategory === 'created-by-me'
                  ? 'bg-white text-slate-900 shadow-xs border border-slate-200/80'
                  : 'text-slate-600 hover:text-slate-900'
                }`}
            >
              <FiPlusCircle className="text-sm" />
              <span>Created by Me</span>
            </button>

            {/* 3. Assigned to Me */}
            <button
              onClick={() => {
                setActiveCategory('my-tasks');
                setTeamFilter('ALL');
                setEmployeeFilter('ALL');
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer ${activeCategory === 'my-tasks'
                  ? 'bg-white text-slate-900 shadow-xs border border-slate-200/80'
                  : 'text-slate-600 hover:text-slate-900'
                }`}
            >
              <FiUser className="text-sm" />
              <span>Assigned to Me</span>
            </button>
          </>
        ) : (
          <>
            {/* Regular User: Assigned to Me, Created by Me, Team (Copied to Me) */}
            <button
              onClick={() => {
                setActiveCategory('my-tasks');
                setTeamFilter('ALL');
                setEmployeeFilter('ALL');
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer ${activeCategory === 'my-tasks'
                  ? 'bg-white text-slate-900 shadow-xs border border-slate-200/80'
                  : 'text-slate-600 hover:text-slate-900'
                }`}
            >
              <FiUser className="text-sm" />
              <span>Assigned to Me</span>
            </button>

            <button
              onClick={() => {
                setActiveCategory('created-by-me');
                setTeamFilter('ALL');
                setEmployeeFilter('ALL');
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer ${activeCategory === 'created-by-me'
                  ? 'bg-white text-slate-900 shadow-xs border border-slate-200/80'
                  : 'text-slate-600 hover:text-slate-900'
                }`}
            >
              <FiPlusCircle className="text-sm" />
              <span>Created by Me</span>
            </button>
          </>
        )}
      </div>

      {/* Main Body */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs">

        {/* Controls Bar: Search + Filter Toggle Button + Active Filter Chips */}
        <div className="p-4 sm:p-5 border-b border-slate-100 bg-slate-50/50 space-y-3 rounded-t-2xl">

          {/* Top Row: Search on Left, Status Tags moved towards Filters on Right (Pic 1) */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">

            {/* Left: Search Input */}
            <div className="relative w-full sm:w-60 lg:w-64 shrink-0">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3.5 py-1.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all shadow-2xs"
              />
            </div>

            {/* Right Group: Status Tags moved towards Filters + Compact Filters Button */}
            <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2.5 flex-1 min-w-0">
              {/* Status Tags (Clean minimal borders, zero harsh black rings) */}
              <div className="flex items-center gap-1.5 overflow-x-auto py-0.5 max-w-full">
                <button
                  type="button"
                  onClick={() => setStatusFilter('ALL')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border ${statusFilter === 'ALL'
                      ? 'bg-slate-100 text-slate-800 border-slate-300 shadow-2xs'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                    }`}
                >
                  <span>All</span>
                  <span className={`font-bold ${statusFilter === 'ALL' ? 'text-slate-900' : 'text-slate-500'}`}>
                    {stats.total}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setStatusFilter(prev => prev === 'TO_DO' ? 'ALL' : 'TO_DO')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border ${statusFilter === 'TO_DO'
                      ? 'bg-amber-50 text-amber-800 border-amber-300 shadow-2xs'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-amber-50/50 hover:text-amber-800 hover:border-amber-200'
                    }`}
                >
                  <span>To Do</span>
                  <span className={`font-bold ${statusFilter === 'TO_DO' ? 'text-amber-900' : 'text-amber-700'}`}>
                    {stats.todo}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setStatusFilter(prev => prev === 'IN_PROGRESS' ? 'ALL' : 'IN_PROGRESS')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border ${statusFilter === 'IN_PROGRESS'
                      ? 'bg-blue-50 text-blue-800 border-blue-300 shadow-2xs'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-blue-50/50 hover:text-blue-800 hover:border-blue-200'
                    }`}
                >
                  <span>In Progress</span>
                  <span className={`font-bold ${statusFilter === 'IN_PROGRESS' ? 'text-blue-900' : 'text-blue-700'}`}>
                    {stats.inProgress}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setStatusFilter(prev => prev === 'COMPLETED' ? 'ALL' : 'COMPLETED')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border ${statusFilter === 'COMPLETED'
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300 shadow-2xs'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-emerald-50/50 hover:text-emerald-800 hover:border-emerald-200'
                    }`}
                >
                  <span>Completed</span>
                  <span className={`font-bold ${statusFilter === 'COMPLETED' ? 'text-emerald-900' : 'text-emerald-700'}`}>
                    {stats.completed}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setStatusFilter(prev => prev === 'OVERDUE' ? 'ALL' : 'OVERDUE')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border ${statusFilter === 'OVERDUE'
                      ? 'bg-rose-50 text-rose-800 border-rose-300 shadow-2xs'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-rose-50/50 hover:text-rose-800 hover:border-rose-200'
                    }`}
                >
                  <span>Overdue</span>
                  <span className={`font-bold ${statusFilter === 'OVERDUE' ? 'text-rose-900' : 'text-rose-700'}`}>
                    {stats.overdue}
                  </span>
                </button>
              </div>

              {/* Filters Button */}
              <div className="relative shrink-0">
                <button
                  ref={filterBtnRef}
                  type="button"
                  onClick={handleToggleFilter}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all border cursor-pointer ${activeFilterCount > 0
                      ? 'border-blue-500 text-blue-700 bg-blue-50/90 font-bold shadow-xs ring-1 ring-blue-500/30'
                      : 'border-slate-300 text-slate-800 bg-slate-100/90 hover:bg-slate-200 hover:border-slate-400 shadow-2xs'
                    }`}
                  title="Configure task filters"
                >
                  <FiFilter className="text-xs text-slate-700 stroke-[2.2]" />
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="w-4.5 h-4.5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {/* Filter Popover Dialog - Portaled to document.body so it NEVER clips */}
                {isFilterOpen && typeof document !== 'undefined' && createPortal(
                  <div
                    ref={filterPopoverRef}
                    style={{
                      top: `${Math.min(popoverPos.top, window.innerHeight - 380)}px`,
                      right: `${popoverPos.right}px`,
                      maxHeight: `calc(100vh - ${Math.min(popoverPos.top, window.innerHeight - 380) + 16}px)`
                    }}
                    className="fixed w-80 sm:w-88 max-w-[calc(100vw-32px)] p-4 bg-white rounded-2xl border border-slate-200 shadow-2xl z-[99999] flex flex-col animate-scale-in"
                  >
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2.5 shrink-0">
                      <div className="flex items-center gap-1.5">
                        <FiFilter className="text-slate-500 text-xs" />
                        <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Filters</h4>
                      </div>
                      {activeFilterCount > 0 && (
                        <button
                          type="button"
                          onClick={clearAllFilters}
                          className="text-xs text-blue-600 hover:text-blue-800 font-semibold cursor-pointer"
                        >
                          Reset
                        </button>
                      )}
                    </div>

                    {/* Filter Fields - Scrollable body if screen is compact */}
                    <div className="space-y-3 text-xs overflow-y-auto py-2 pr-1 flex-1">
                      {/* Status Filter */}
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                          Status:
                        </label>
                        <select
                          value={statusFilter}
                          onChange={e => setStatusFilter(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                        >
                          <option value="ALL">All Statuses</option>
                          <option value="TO_DO">To Do</option>
                          <option value="IN_PROGRESS">In Progress</option>
                          <option value="COMPLETED">Completed</option>
                          <option value="BLOCKED">Blocked</option>
                          <option value="OVERDUE">Overdue</option>
                        </select>
                      </div>
                      {/* Team & Employee Filters: ONLY on Team Tasks for SuperAdmin / Admin / Manager */}
                      {activeCategory === 'team-tasks' && canViewTeamTasks && (
                        <>
                          {/* By Team */}
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                              Team / Department:
                            </label>
                            <select
                              data-testid="popover-team-filter-select"
                              aria-label="Filter by team"
                              value={teamFilter}
                              onChange={e => {
                                const newTeam = e.target.value;
                                setTeamFilter(newTeam);
                                if (newTeam !== 'ALL' && employeeFilter !== 'ALL') {
                                  const emp = users.find(u => u.username === employeeFilter);
                                  if (emp && emp.department && emp.department.toUpperCase() !== newTeam.toUpperCase()) {
                                    setEmployeeFilter('ALL');
                                  }
                                }
                              }}
                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                            >
                              <option value="ALL">All Teams</option>
                              {uniqueTeams.map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>

                          {/* By Employee */}
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                              Employee:
                            </label>
                            <select
                              data-testid="popover-employee-filter-select"
                              aria-label="Filter by employee"
                              value={employeeFilter}
                              onChange={e => setEmployeeFilter(e.target.value)}
                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                            >
                              <option value="ALL">All Employees</option>
                              {uniqueEmployees.map(emp => (
                                <option key={emp.username} value={emp.username}>
                                  {emp.fullName} ({emp.username}){emp.department ? ` • ${emp.department}` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}

                      {/* Project Filter */}
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                          Project:
                        </label>
                        <select
                          value={projectFilter}
                          onChange={e => setProjectFilter(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                        >
                          <option value="ALL">All Projects</option>
                          {projectsList.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.projectName || p.name || `Project #${p.id}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Product Filter */}
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                          Product:
                        </label>
                        <select
                          value={productFilter}
                          onChange={e => setProductFilter(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                        >
                          <option value="ALL">All Products</option>
                          {productsList.map(prod => (
                            <option key={prod.id} value={prod.id}>
                              {prod.productname || prod.name || `Product #${prod.id}`}
                            </option>
                          ))}
                        </select>
                      </div>



                      {/* Priority Filter */}
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                          Priority:
                        </label>
                        <select
                          value={priorityFilter}
                          onChange={e => setPriorityFilter(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                        >
                          <option value="ALL">All Priorities</option>
                          <option value="CRITICAL">Critical</option>
                          <option value="HIGH">High</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="LOW">Low</option>
                        </select>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-slate-100 flex justify-end shrink-0">
                      <button
                        type="button"
                        onClick={() => setIsFilterOpen(false)}
                        className="w-full py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-xs cursor-pointer"
                      >
                        Done
                      </button>
                    </div>
                  </div>,
                  document.body
                )}
              </div>


            </div>

          </div>

          {/* Bottom Row: Active Filter Pills / Chips (Like Screenshot) */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {activeFilters.map(filter => (
                <span
                  key={filter.key}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200/80 shadow-2xs"
                >
                  <span>{filter.label}</span>
                  <button
                    type="button"
                    onClick={filter.onRemove}
                    className="p-0.5 hover:bg-blue-100 rounded-full transition-colors cursor-pointer text-blue-600 hover:text-blue-900"
                    title={`Remove ${filter.key} filter`}
                  >
                    <FiX className="text-xs" />
                  </button>
                </span>
              ))}

              <button
                type="button"
                onClick={clearAllFilters}
                className="text-xs text-slate-500 hover:text-slate-800 font-medium underline cursor-pointer ml-1"
              >
                Clear all
              </button>
            </div>
          )}

        </div>

        {/* Reorganized Table Column Headers with Created By neatly fitted */}
        {!loading && !error && filteredTasks.length > 0 && (
          <div className="hidden sm:grid sm:grid-cols-12 gap-3 lg:gap-4 px-6 py-3 bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider select-none">
            <div className="sm:col-span-3">Task & Deliverable</div>
            <div className="sm:col-span-2 text-center">Status</div>
            <div className="sm:col-span-1 text-center">Priority</div>
            <div className="sm:col-span-2 text-center">Assignee</div>
            <div className="sm:col-span-2 text-center">Created By</div>
            <div className="sm:col-span-2 text-center">Due Date</div>
          </div>
        )}

        {/* Task Cards Grid / List */}
        {loading ? (
          <div className="py-20 text-center text-slate-500 space-y-2">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm font-medium">Loading tasks...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-rose-500 bg-rose-50/50 m-6 rounded-2xl border border-rose-100">
            <p className="font-semibold">{error}</p>
            <button
              onClick={fetchTasks}
              className="mt-3 px-4 py-2 bg-white border border-rose-200 text-rose-600 rounded-xl text-xs font-bold hover:bg-rose-50 cursor-pointer"
            >
              Retry
            </button>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="py-20 text-center space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center text-2xl mx-auto border border-slate-200">
              <TaskChecklistIcon size={24} />
            </div>
            <h3 className="text-base font-bold text-slate-700">No tasks found</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              {search || statusFilter !== 'ALL' || priorityFilter !== 'ALL'
                ? 'No tasks match your selected filter criteria. Try clearing filters.'
                : activeCategory === 'team-tasks'
                  ? 'No tasks created across your team yet.'
                  : 'Get started by creating a deliverable or assignment.'}
            </p>
            {activeCategory !== 'team-tasks' && !search && statusFilter === 'ALL' && priorityFilter === 'ALL' && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-xl transition-all shadow-xs cursor-pointer mt-2"
              >
                <FiPlus className="text-base" />
                <span>Create First Task</span>
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {paginatedTasks.map((task, idx) => {
              const statusKey = STATUS_META[task.taskStatus] ? task.taskStatus : 'TO_DO';
              const sm = STATUS_META[statusKey];

              const prioKey = PRIORITY_META[task.priority] ? task.priority : 'MEDIUM';
              const pm = PRIORITY_META[prioKey];

              const isOverdue = Boolean(
                task.deadLine &&
                task.taskStatus !== 'COMPLETED' &&
                new Date(task.deadLine).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
              );

              const normalizeUsername = (u) => String(u || '').trim().toLowerCase().split('@')[0];
              const myNormUser = normalizeUsername(currentUsername);
              const taskCreatorNorm = normalizeUsername(task.createdBy || task.createdByName);

              const canEdit = canEditTask(task, currentUsername);
              const isCreator = Boolean(taskCreatorNorm && myNormUser && taskCreatorNorm === myNormUser);
              const canDelete = isCreator;

              return (
                <div
                  key={task.taskId}
                  onClick={() => navigate(`/my-space/tasks/${task.taskId}`, { state: { task, from: '/my-space/tasks' } })}
                  className={`p-3.5 sm:py-3 sm:px-6 sm:h-[4.75rem] sm:min-h-[4.75rem] transition-colors cursor-pointer group flex flex-col gap-2.5 sm:grid sm:grid-cols-12 sm:gap-3 lg:gap-4 sm:items-center ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                    } hover:bg-blue-50/40`}
                >
                  {/* Column 1: Task Name + Project/Product Badges + Minimal Hover Description (sm:col-span-3) */}
                  <div className="sm:col-span-3 min-w-0 flex flex-col justify-center h-full">


                    {/* Task Name with Floating Hover Description Overlay */}
                    <div className="relative group/desc inline-block max-w-full">
                      <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors text-xs sm:text-sm truncate cursor-pointer">
                        {task.title}
                      </h3>

                      {/* Floating Description on Hover with Minimal Design */}
                      {task.description && task.description.trim() ? (
                        <div className="absolute left-0 top-full mt-1.5 hidden group-hover/desc:block z-40 w-72 sm:w-96 p-3 bg-white/95 backdrop-blur-md rounded-xl border border-slate-200/90 shadow-xl text-xs text-slate-600 font-normal leading-relaxed pointer-events-none transition-all">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 flex items-center justify-between border-b border-slate-100 pb-1">
                            <span>Description</span>
                            <span className="text-[9px] text-slate-400 font-mono">TS-{task.taskId}</span>
                          </div>
                          <p className="whitespace-pre-wrap line-clamp-5 text-slate-700">{task.description}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Column 2: Sleek Status Dropdown / Pill (col-span-2) */}
                  <div className="hidden sm:flex sm:col-span-2 items-center justify-center" onClick={e => e.stopPropagation()}>
                    {canEdit ? (
                      <div className="relative inline-flex items-center">
                        <select
                          value={statusKey}
                          onChange={e => handleUpdateStatus(e, task, e.target.value)}
                          onClick={e => e.stopPropagation()}
                          className={`appearance-none w-[94px] pl-2 pr-5 py-1 text-center rounded-lg text-[11px] font-semibold border outline-none transition-all shadow-2xs ${sm.bg} ${sm.color} ${sm.border} cursor-pointer hover:opacity-90`}
                          title="Click to change status"
                        >
                          <option value="TO_DO" className="bg-white text-slate-800 font-medium">To Do</option>
                          <option value="IN_PROGRESS" className="bg-white text-slate-800 font-medium">In Progress</option>
                          <option value="COMPLETED" className="bg-white text-slate-800 font-medium">Completed</option>
                          <option value="BLOCKED" className="bg-white text-slate-800 font-medium">Blocked</option>
                        </select>
                        <FiChevronDown className={`pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] stroke-[2.5] ${sm.color} opacity-75`} />
                      </div>
                    ) : (
                      <div className="relative inline-flex items-center" title="Editing task status is restricted to assignee and creator">
                        <select
                          disabled
                          value={statusKey}
                          className={`appearance-none w-[94px] pl-2 pr-5 py-1 text-center rounded-lg text-[11px] font-semibold border outline-none opacity-80 cursor-not-allowed ${sm.bg} ${sm.color} ${sm.border}`}
                          title="Editing task status is restricted to assignee and creator"
                        >
                          <option value={statusKey} className="bg-white text-slate-800 font-medium">{sm.label}</option>
                        </select>
                        <FiChevronDown className={`pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] stroke-[2.5] ${sm.color} opacity-50`} />
                      </div>
                    )}
                  </div>

                  {/* Column 3: Priority Badge - Clean Text, No Arrows (col-span-1) */}
                  <div className="hidden sm:flex sm:col-span-1 items-center justify-center">
                    <span className={`inline-flex items-center justify-center w-[74px] py-0.5 text-center rounded-md text-xs font-medium border ${pm.bg} ${pm.color} ${pm.border}`}>
                      {pm.label}
                    </span>
                  </div>

                  {/* Column 4: Assignee - If lengthier, wraps to two rows (Pic 2) */}
                  <div className="hidden sm:flex sm:col-span-2 items-center justify-center min-w-0 text-center px-1">
                    <span
                      className={`text-xs font-semibold leading-tight line-clamp-2 break-words max-w-[105px] ${task.username ? 'text-slate-800' : 'text-slate-400 italic'}`}
                      title={task.assigneeName || task.username || 'Unassigned'}
                    >
                      {task.assigneeName || task.username || 'Unassigned'}
                    </span>
                  </div>

                  {/* Column 5: Created By - Username stripped of @domain with full username on hover */}
                  <div className="hidden sm:flex sm:col-span-2 items-center justify-center min-w-0 text-center px-1">
                    <span
                      className={`text-xs font-medium leading-tight line-clamp-2 break-words max-w-[105px] ${task.createdBy ? 'text-slate-600' : 'text-slate-400 italic'}`}
                      title={task.createdBy || 'System'}
                    >
                      {task.createdBy ? String(task.createdBy).split('@')[0] : 'System'}
                    </span>
                  </div>

                  {/* Column 6: Due Date (col-span-2) - Centered with Overdue on Second Row (Delete button removed) */}
                  <div className="hidden sm:flex sm:col-span-2 items-center justify-center relative h-full">
                    <div className="flex flex-col items-center justify-center text-center">
                      {task.deadLine ? (
                        <>
                          <span className={`text-xs whitespace-nowrap leading-tight ${isOverdue ? 'text-rose-600 font-semibold' : 'text-slate-600 font-medium'
                            }`}>
                            {fmtDate(task.deadLine)}
                          </span>
                          {isOverdue && (
                            <span className="mt-0.5 text-[10px] font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-200 whitespace-nowrap leading-tight">
                              Overdue
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">No Due Date</span>
                      )}
                    </div>
                  </div>

                  {/* Mobile View (< sm) */}
                  <div className="flex sm:hidden flex-col gap-2 pt-2 border-t border-slate-100 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        {canEdit ? (
                          <div className="relative inline-flex items-center">
                            <select
                              value={statusKey}
                              onChange={e => handleUpdateStatus(e, task, e.target.value)}
                              onClick={e => e.stopPropagation()}
                              className={`appearance-none w-[90px] pl-2 pr-5 py-0.5 text-center rounded text-[11px] font-semibold border outline-none ${sm.bg} ${sm.color} ${sm.border} cursor-pointer`}
                              title="Click to change status"
                            >
                              <option value="TO_DO" className="bg-white text-slate-800">To Do</option>
                              <option value="IN_PROGRESS" className="bg-white text-slate-800">In Progress</option>
                              <option value="COMPLETED" className="bg-white text-slate-800">Completed</option>
                              <option value="BLOCKED" className="bg-white text-slate-800">Blocked</option>
                            </select>
                            <FiChevronDown className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] stroke-[2.5] ${sm.color} opacity-75`} />
                          </div>
                        ) : (
                          <div className="relative inline-flex items-center" title="Editing task status is restricted to assignee and creator">
                            <select
                              disabled
                              value={statusKey}
                              className={`appearance-none w-[90px] pl-2 pr-5 py-0.5 text-center rounded text-[11px] font-semibold border outline-none opacity-80 cursor-not-allowed ${sm.bg} ${sm.color} ${sm.border}`}
                              title="Editing task status is restricted to assignee and creator"
                            >
                              <option value={statusKey} className="bg-white text-slate-800">{sm.label}</option>
                            </select>
                            <FiChevronDown className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] stroke-[2.5] ${sm.color} opacity-50`} />
                          </div>
                        )}
                        <span className={`inline-flex items-center justify-center w-[70px] py-0.5 text-center rounded text-[11px] font-medium border ${pm.bg} ${pm.color} ${pm.border}`}>
                          {pm.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-slate-600">
                        <span className="text-xs truncate max-w-[110px] font-medium">{task.username || 'Unassigned'}</span>
                      </div>
                    </div>

                    {task.deadLine && (
                      <div className="flex items-center justify-between text-[11px] text-slate-400">
                        <span className="flex items-center gap-1">
                          <FiCalendar size={11} className={isOverdue ? 'text-rose-500' : 'text-slate-400'} />
                          <span className={isOverdue ? 'text-rose-600 font-semibold' : 'text-slate-600'}>
                            {fmtDate(task.deadLine)}
                          </span>
                        </span>
                        {isOverdue && (
                          <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.2 rounded border border-rose-200">
                            Overdue
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                </div>
              );
            })}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && !error && totalFilteredTasks > 0 && (
          <div className="px-5 py-3.5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4 bg-slate-50/60 rounded-b-2xl">
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span>
                Showing <span className="font-bold text-slate-700">{(currentPage - 1) * pageSize + 1}</span>–<span className="font-bold text-slate-700">{Math.min(currentPage * pageSize, totalFilteredTasks)}</span> of <span className="font-bold text-slate-700">{totalFilteredTasks}</span> tasks
              </span>
              <div className="flex items-center gap-1.5 ml-2">
                <span className="text-slate-400">Show:</span>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-0.5 text-xs font-semibold bg-white border border-slate-200 rounded-md text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors"
                >
                  Prev
                </button>

                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) {
                    p = i + 1;
                  } else if (currentPage <= 3) {
                    p = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    p = totalPages - 4 + i;
                  } else {
                    p = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-bold transition-all ${currentPage === p
                          ? 'bg-blue-600 text-white shadow-xs'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                    >
                      {p}
                    </button>
                  );
                })}

                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Task Creation Modal / Side Drawer */}
      <CreateTaskModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTask}
        users={users}
      />

      {/* Confirm Deletion Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, taskId: null, taskTitle: '', loading: false })}
        onConfirm={handleConfirmDeleteTask}
        title="Delete Task?"
        message={`Are you sure you want to permanently delete "${deleteConfirm.taskTitle}"? All subtasks, comments, and attachments will also be deleted.`}
        confirmText="Delete"
        loading={deleteConfirm.loading}
        variant="danger"
      />

    </div>
  );
}
